import type { Business, ScoreSnapshot } from '../contract';
import { useMemo } from 'react';
import { ShareIcon } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../primitives/Card';
import { PageHeader } from '../primitives/PageHeader';
import { Chip } from '../primitives/Chip';
import { Button } from '../primitives/Button';
import { ScoreDial } from '../components/ScoreDial';
import { LevelLadder } from '../components/LevelLadder';
import { PillarBars } from '../components/PillarBars';
import { KpiRow } from '../components/KpiRow';
import { TrendChart } from '../components/TrendChart';
import { AttentionRow } from '../components/AttentionRow';
import { ActivityFeed } from '../components/ActivityFeed';
import { GateBanner } from '../components/GateBanner';
import { GapBanner } from '../components/GapBanner';
import { HeadToHead } from '../components/HeadToHead';
import { ScanFreshness } from '../components/ScanFreshness';
import { useActionDrawer } from '../components/ActionDrawer';
import { monthLabel } from '../lib/oura';
import type { View } from '../nav';

export function Dashboard({
  snapshot, business, onNavigate, onOpenReport, embedded,
}: {
  snapshot: ScoreSnapshot;
  business: Business;
  onNavigate: (v: View) => void;
  onOpenReport: () => void;
  /** Rendered inside BusinessDetail (staff drill-in) — the chrome owns the header. */
  embedded?: boolean;
}) {
  const { openAction } = useActionDrawer();
  const { gated, headline } = snapshot;

  const topActions = snapshot.actions.filter((a) => a.status !== 'done').slice(0, 3);
  const namedCount = Math.round(headline.namedRate * headline.questionsTested);
  const showGap = !gated && headline.bestGooglePosition !== null && headline.bestGooglePosition <= 3 && headline.namedRate < 0.4;

  const rankedRivals = useMemo(
    () => [...snapshot.rivals].sort((a, b) => b.winsQuestionIds.length - a.winsQuestionIds.length),
    [snapshot.rivals],
  );
  const projected = snapshot.history.filter((h) => h.projected);
  const questionText = (id: string) => snapshot.questions.find((q) => q.id === id)?.text ?? id;

  const reviewStat = snapshot.setup.reputation.find((s) => s.label.toLowerCase().includes('review'));
  const reviewCount = reviewStat ? Number(reviewStat.value.replace(/[^0-9]/g, '')) || null : null;
  const ratingStat = snapshot.setup.reputation.find((s) => s.label.toLowerCase().includes('rating'));
  const reviewRating = ratingStat ? Number(ratingStat.value.replace(/[^0-9.]/g, '')) || null : null;

  return (
    <div>
      {!embedded && (
        <PageHeader
          title="Dashboard"
          subtitle={`How ${business.name} is showing up when people ask AI and search Google.`}
          action={
            <Button variant="secondary" onClick={onOpenReport}>
              <ShareIcon className="h-4 w-4" />
              Share report
            </Button>
          }
        />
      )}

      {!gated && <ScanFreshness capturedAt={snapshot.capturedAt} />}
      {showGap && (
        <GapBanner
          googlePosition={headline.bestGooglePosition!}
          namedCount={namedCount}
          questionsTested={headline.questionsTested}
          onSeeQuestions={() => onNavigate('questions')}
        />
      )}

      {!gated && <KpiRow snapshot={snapshot} reviewCount={reviewCount} reviewRating={reviewRating} />}

      {/* Score + attention */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHeader title="Where you stand" aside={<Chip className="border-blue-200 bg-blue-50 text-blue-700">out of 100</Chip>} />
          <CardBody>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="flex flex-col items-center justify-center gap-5">
                <ScoreDial
                  total={snapshot.total}
                  plusMinus={snapshot.plusMinus}
                  level={snapshot.level}
                  levelName={snapshot.levelName}
                  previousTotal={snapshot.previousTotal}
                />
                <div className="w-full"><LevelLadder level={snapshot.level} /></div>
              </div>
              <div className="flex flex-col justify-center">
                <PillarBars pillars={gated ? snapshot.pillars.filter((p) => p.kind === 'lever') : snapshot.pillars} />
              </div>
            </div>
            <p className="mt-5 border-t border-gray-100 pt-4 text-sm text-gray-500">{headline.plainSummary}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Needs your attention"
            aside={topActions.length > 0 && <Chip className="border-amber-200 bg-amber-50 text-amber-700">{topActions.length}</Chip>}
          />
          <CardBody>
            {topActions.length > 0 ? (
              topActions.map((a) => <AttentionRow key={a.id} action={a} onFix={() => openAction(a.id)} />)
            ) : (
              <p className="py-6 text-center text-sm text-gray-400">Nothing needs your attention right now.</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Why the deeper checks are paused — a calm explainer under the score,
          not a red alarm above it. */}
      {gated && (
        <div className="mt-6">
          <GateBanner reasons={snapshot.gateReasons} onSeeFixes={() => onNavigate('todo')} />
        </div>
      )}

      {/* Trend + progress, and activity */}
      {!gated && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Your progress" aside={<span className="text-xs text-gray-400">monthly scorecards</span>} />
            <CardBody>
              <TrendChart history={snapshot.history} />
              {projected.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">What's next</p>
                  <div className="space-y-2">
                    {projected.map((p) => (
                      <div key={p.month} className="flex items-start gap-3 text-sm">
                        <span className="w-20 flex-shrink-0 text-gray-500">{monthLabel(p.month)}</span>
                        <span className="text-gray-300">—</span>
                        <span className="text-gray-500">{p.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Recent activity" aside={<span className="text-xs text-gray-400">auto-tracked</span>} />
            <CardBody><ActivityFeed feed={snapshot.feed} /></CardBody>
          </Card>
        </div>
      )}

      {/* Who wins instead — folded in from its own tab */}
      {!gated && rankedRivals.length > 0 && (
        <Card className="mt-6">
          <CardHeader
            title="Who wins instead"
            aside={<span className="text-xs text-gray-400">what to do about your closest rival</span>}
          />
          <CardBody>
            <HeadToHead you={business.name} rival={rankedRivals[0]} questionText={questionText} />
            {rankedRivals.length > 1 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                <span className="text-xs text-gray-400">Others named for your questions:</span>
                {rankedRivals.slice(1).map((r) => (
                  <Chip key={r.domain} className="border-gray-200 bg-gray-50 text-gray-600">
                    {r.name} · {r.winsQuestionIds.length}
                  </Chip>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
